import { BlockingStateDto, BorrowerNaturalPersonDto } from 'api/borrower';
import { FileTypeEnum } from 'api/actor';
import { QuestionAnswerDto } from 'api/quote';
import { useAppSelector } from 'app/hooks';
import { mapActorTypeBorrower } from 'features/new-actor/mappers/optim/mapActorTypeBorrower';
import { mapBorrowerNaturalPersonFormValues } from 'features/new-actor/mappers/optim/mapBorrowerNaturalPersonFormValues';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';
import { useBorrowerProjectId } from 'features/subscription-process/hooks/useBorrowerProjectId';
import { FormikProps } from 'formik';
import { usePanel } from 'hooks/usePanel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { actorsAPI, borrowersAPI, filesAPI } from 'services/swagger-api';
import { ProfilingMode } from '../../slices/types/enums';
import {
  ContextCode,
  SurveyName,
  useGenericSurvey
} from '../../hooks/useGenericSurvey';
import { DocumentsFormFieldNames } from 'features/new-actor/enums/documentsFormFieldNames';
import { ActorType } from 'features/new-actor/enums/actorType';
import {
  BROKER_CONFIRMATION_FIELD,
  BorrowerInsuredWithDocumentsFormValues
} from './borrowerInsuredWithDocuments.config';

const RISK_PROFESSION_QUESTION_KEY = 'riskProfession';
/**
 * Clé de la réponse "Aucune" pour la question riskProfession (alignée back/BDD).
 */
const RISK_PROFESSION_NONE_ANSWER_KEY = 'none';

/**
 * Extrait l'ensemble des clés de réponses sélectionnées pour la question
 * `riskProfession` à partir des valeurs Formik.
 *
 * `values.surveyForm` est indexé par `question.key` et chaque entrée est un
 * tableau d'objets `{ id, key, label }` (PossibleAnswerWithLabel).
 */
function getRiskProfessionAnswerKeys(
  values: BorrowerInsuredWithDocumentsFormValues | undefined
): Set<string> {
  const surveyForm = (values as any)?.surveyForm;
  const raw = surveyForm?.[RISK_PROFESSION_QUESTION_KEY];
  const items = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
  const keys = new Set<string>();
  for (const item of items) {
    if (item && typeof item === 'object' && 'key' in item) {
      keys.add(String((item as { key: string | number }).key));
    } else if (typeof item === 'string' || typeof item === 'number') {
      keys.add(String(item));
    }
  }
  return keys;
}

/**
 * Vrai si la sélection ne contient strictement que "Aucune".
 */
function isOnlyNone(keys: Set<string>): boolean {
  return keys.size === 1 && keys.has(RISK_PROFESSION_NONE_ANSWER_KEY);
}

/**
 * Vrai si on passe de "Aucune (seule)" à au moins une profession à risque.
 * On ne déclenche pas la confirmation si l'utilisateur vide complètement la
 * réponse (next.size === 0) — pas de profession = pas d'annulation côté back.
 */
function detectRiskProfessionEscalation(
  previousValues: BorrowerInsuredWithDocumentsFormValues | undefined,
  newValues: BorrowerInsuredWithDocumentsFormValues
): boolean {
  const prev = getRiskProfessionAnswerKeys(previousValues);
  const next = getRiskProfessionAnswerKeys(newValues);
  if (!isOnlyNone(prev)) return false;
  if (next.size === 0) return false;
  return !isOnlyNone(next);
}

function mapSurveyAnswers(
  questionId: string,
  value: any
): QuestionAnswerDto[] {
  if (Array.isArray(value)) {
    return value.map((item) => mapSingleAnswer(questionId, item));
  }

  return [mapSingleAnswer(questionId, value)];
}

function mapSingleAnswer(questionId: string, value: any): QuestionAnswerDto {
  if (value && typeof value === 'object' && value.key) {
    return {
      questionId,
      possibleAnswerId: value.id
    };
  }

  if (typeof value === 'boolean') {
    return {
      questionId,
      answerText: value.toString()
    };
  }

  return {
    questionId,
    answerText: value?.toString() || ''
  };
}

export const useBorrowerInsuredWithDocuments = (
  personId: string,
  onSuccess?: () => void,
  borrowerProjectIdProp?: string
) => {
  // Id du projet : prop de l'ouvrant en priorité (fiable en panneau), sinon l'URL
  // (window.location, jamais le store Redux dont `riskId` est l'emprunteurId).
  const borrowerProjectIdFromUrl = useBorrowerProjectId();
  const borrowerProjectId = borrowerProjectIdProp ?? borrowerProjectIdFromUrl;
  const { getSurvey, transformFormValues, mapToSurveyFormValue } =
    useGenericSurvey();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const referentialData = useAppSelector(selectReferentialData);

  const { setPanelActions, closePanel } = usePanel();
  const formikRef = useRef<FormikProps<BorrowerInsuredWithDocumentsFormValues>>(null);
  const { initSurvey } = useGenericSurvey();

  const [pendingBorrower, setPendingBorrower] =
    useState<BorrowerNaturalPersonDto>();
  const [answers, setAnswers] = useState<Array<QuestionAnswerDto>>([]);
  const [documents, setDocuments] = useState<{
    cni?: any;
    rib?: any;
  }>({});
  /**
   * Valeurs en attente lorsqu'on doit demander une confirmation à l'utilisateur
   * (changement riskProfession Aucune → autre). null = pas de confirmation active.
   */
  const [pendingSubmitValues, setPendingSubmitValues] =
    useState<BorrowerInsuredWithDocumentsFormValues | null>(null);
  /**
   * Blocage métier renvoyé par le back après le submit (devis annulé). Quand
   * non-null, on affiche la modale d'info ; sa fermeture déclenche le refresh
   * et la fermeture du panneau.
   */
  const [blockingState, setBlockingState] = useState<BlockingStateDto | null>(
    null
  );

  // Init survey
  useEffect(() => {
    initSurvey(
      ContextCode.BorrowerPerson,
      SurveyName.BorrowerPerson,
      ProfilingMode.Group
    );
  }, [initSurvey]);

  // Fetch borrower person with documents
  useEffect(() => {
    const fetchBorrowerWithDocuments = async () => {
      // Sans id de personne/projet on ne peut pas charger : on coupe le spinner au lieu
      // de le laisser tourner indéfiniment (sinon panneau bloqué en chargement).
      if (!personId || !borrowerProjectId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch borrower data with questions
        const borrowerResponse = await borrowersAPI.getBorrowerProjectPerson({
          borrowerProjectId,
          personId,
          includeQuestions: true
        });

        if (borrowerResponse.naturalPerson) {
          setPendingBorrower(borrowerResponse.naturalPerson);
          if (borrowerResponse?.saveAnswersCommand) {
            setAnswers(borrowerResponse.saveAnswersCommand.answers);
          }
        }

        // Fetch documents
        const personWithDocuments = await actorsAPI.getPersonByID({
          personId,
          includeDocuments: true
        });

        if (personWithDocuments?.documents) {
          const cni = personWithDocuments.documents.find(
            (doc) => doc.fileType === FileTypeEnum.PieceIdentite
          );
          const rib = personWithDocuments.documents.find(
            (doc) => doc.fileType === FileTypeEnum.Rib
          );
          setDocuments({ cni, rib });
        }
      } catch (error: any) {
        console.error('Error fetching borrower with documents:', error);
        toast.error(
          error?.response?.error?.message ||
            'Erreur lors du chargement des données'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchBorrowerWithDocuments();
  }, [personId, borrowerProjectId]);

  // Upload document
  const uploadDocument = useCallback(
    async (
      file: File,
      fileType: FileTypeEnum,
      naturalPersonId: string
    ): Promise<boolean> => {
      try {
        const response = await filesAPI.uploadFileByType({ fileType, file });

        if (response.isSuccess && response.id) {
          await actorsAPI.createPersonDocuments({
            personId: naturalPersonId,
            storedFileIdRequest: { storedFileId: response.id }
          });
          return true;
        }
        return false;
      } catch (error) {
        console.error(`Error uploading ${fileType}:`, error);
        return false;
      }
    },
    []
  );

  // Upload new documents
  const uploadNewDocuments = useCallback(
    async (values: BorrowerInsuredWithDocumentsFormValues): Promise<void> => {
      const docs = values[DocumentsFormFieldNames.Documents];
      const uploadPromises: Promise<boolean>[] = [];

      // CNI - upload only if it's a new file
      const cniFile = docs?.[DocumentsFormFieldNames.Cni]?.file;
      const hadCni = documents?.cni?.storedFileId;
      if (cniFile instanceof File && !hadCni) {
        uploadPromises.push(
          uploadDocument(cniFile, FileTypeEnum.PieceIdentite, personId)
        );
      }

      // RIB - upload only if it's a new file
      const ribFile = docs?.[DocumentsFormFieldNames.RIB]?.file;
      const hadRib = documents?.rib?.storedFileId;
      if (ribFile instanceof File && !hadRib) {
        uploadPromises.push(uploadDocument(ribFile, FileTypeEnum.Rib, personId));
      }

      await Promise.all(uploadPromises);
    },
    [documents, personId, uploadDocument]
  );

  /**
   * Exécution effective du submit (upload docs + appel back). Suppose que les
   * éventuels garde-fous (confirmation utilisateur) ont déjà été franchis.
   */
  const performSubmit = useCallback(
    async (values: BorrowerInsuredWithDocumentsFormValues) => {
      if (!borrowerProjectId) return;

      setIsSubmitting(true);
      try {
        // 1. Upload new documents
        await uploadNewDocuments(values);

        // 2. Update borrower person with survey
        const mappedValues = mapBorrowerNaturalPersonFormValues(
          values,
          referentialData
        );
        const survey = await getSurvey(
          ContextCode.BorrowerPerson,
          SurveyName.BorrowerPerson
        );
        const transformedValues = transformFormValues(values);
        let surveyAnswers: Array<QuestionAnswerDto> = [];
        if (transformedValues.surveyForm) {
          surveyAnswers = Object.entries(transformedValues.surveyForm).flatMap(
            ([questionId, answersArray]) =>
              mapSurveyAnswers(questionId, answersArray)
          );
        }

        const response = await borrowersAPI.upsertBorrowerPerson({
          borrowerProjectId,
          upsertBorrowerPersonCommandDto: {
            naturalPerson: { ...mappedValues },
            saveAnswersCommand: {
              answers: surveyAnswers,
              surveyId: survey.id,
              autoEvaluate: true
            }
          }
        });

        // Cas blocage métier (ex. profession à risque → devis annulé) : on
        // garde le panneau ouvert pour montrer la modale d'info ; le refresh
        // + fermeture interviennent à la fermeture de la modale.
        if (response.blockingState?.isBloque) {
          setBlockingState(response.blockingState);
          return;
        }

        onSuccess?.();
        closePanel();
      } catch (error) {
        console.error('Error submitting borrower with documents:', error);
        toast.error('Erreur lors de la sauvegarde');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      borrowerProjectId,
      uploadNewDocuments,
      referentialData,
      getSurvey,
      transformFormValues,
      onSuccess,
      closePanel
    ]
  );

  /**
   * Gate appelé par Formik. Détecte les transitions qui doivent être confirmées
   * (riskProfession passe de Aucune à autre) ; dans ce cas, on diffère l'appel
   * réel jusqu'à la confirmation utilisateur.
   *
   * On lit les `initialValues` directement via la ref Formik pour éviter une
   * dépendance sur le `initialValues` du hook (déclaré plus bas, TDZ).
   */
  const handleSubmit = useCallback(
    async (values: BorrowerInsuredWithDocumentsFormValues) => {
      const formikInitialValues = formikRef.current?.initialValues as
        | BorrowerInsuredWithDocumentsFormValues
        | undefined;
      if (detectRiskProfessionEscalation(formikInitialValues, values)) {
        setPendingSubmitValues(values);
        return;
      }
      await performSubmit(values);
    },
    [performSubmit]
  );

  /**
   * Confirmation utilisateur acquise → on lance le submit qui était en attente.
   */
  const confirmRiskProfessionChange = useCallback(async () => {
    if (!pendingSubmitValues) return;
    const values = pendingSubmitValues;
    setPendingSubmitValues(null);
    await performSubmit(values);
  }, [pendingSubmitValues, performSubmit]);

  /**
   * L'utilisateur annule : on ferme simplement la modale, le panneau reste
   * ouvert avec les valeurs telles qu'il les a saisies.
   */
  const cancelRiskProfessionChange = useCallback(() => {
    setPendingSubmitValues(null);
  }, []);

  /**
   * Fermeture de la modale d'info post-blocage : on rafraîchit le subscription
   * process (timeline revient à "Tarificateur" → les CTAs en aval — bulletin
   * d'adhésion, questionnaire santé — se désactivent via la logique existante)
   * puis on ferme le panneau.
   */
  const closeBlockingStateModal = useCallback(() => {
    setBlockingState(null);
    onSuccess?.();
    closePanel();
  }, [onSuccess, closePanel]);

  // Initial values
  const initialValues = useMemo((): BorrowerInsuredWithDocumentsFormValues | undefined => {
    if (!pendingBorrower) return undefined;

    const mappedResult = mapActorTypeBorrower(
      ActorType.BorrowerNatural,
      pendingBorrower,
      referentialData,
      mapToSurveyFormValue(answers)
    );

    // mapActorTypeBorrower returns { [ActorType.BorrowerNatural]: { ...formValues } }
    const borrowerValues = mappedResult?.[ActorType.BorrowerNatural];
    if (!borrowerValues) return undefined;

    return {
      ...borrowerValues,
      [DocumentsFormFieldNames.Documents]: {
        [DocumentsFormFieldNames.Cni]: documents.cni
          ? {
              storedFileId: documents.cni.storedFileId,
              fileType: documents.cni.fileType,
              verificationStatus: documents.cni.verificationStatus
            }
          : undefined,
        [DocumentsFormFieldNames.RIB]: documents.rib
          ? {
              storedFileId: documents.rib.storedFileId,
              fileType: documents.rib.fileType,
              verificationStatus: documents.rib.verificationStatus
            }
          : undefined
      },
      [BROKER_CONFIRMATION_FIELD]: false
    } as BorrowerInsuredWithDocumentsFormValues;
  }, [pendingBorrower, referentialData, answers, documents, mapToSurveyFormValue]);

  // Panel actions
  useEffect(() => {
    const hasPendingConfirmation = pendingSubmitValues !== null;
    const hasBlockingModal = blockingState !== null;
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled:
        !isFormValid ||
        isSubmitting ||
        isLoading ||
        hasPendingConfirmation ||
        hasBlockingModal,
      onSubmit: () => formikRef.current?.submitForm()
    });
  }, [
    isFormValid,
    isSubmitting,
    isLoading,
    setPanelActions,
    pendingSubmitValues,
    blockingState
  ]);

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  return {
    formikRef,
    initialValues,
    isLoading,
    isSubmitting,
    handleSubmit,
    handleValidationChange,
    // Confirmation pré-submit (riskProfession Aucune → autre)
    isRiskProfessionConfirmOpen: pendingSubmitValues !== null,
    confirmRiskProfessionChange,
    cancelRiskProfessionChange,
    // Blocage post-submit
    blockingState,
    closeBlockingStateModal
  };
};
